<script setup>
import { Head } from '@inertiajs/vue3'
import { thumbor } from '@/Frontend/plugins/thumbor/thumbor'

const breadcrumb = defineAsyncComponent(() => import('@components/Breadcrumb.vue'))

const props = defineProps({
	book: {
		type: Object,
		default: () => {},
	},
	moreBooks: {
		type: Array,
		default: () => [],
	},
})

provide('books', props.moreBooks)

const appStore = useAppStore()

const characteristics = computed(() => {
	let items = {}

	if (props.book.title) items['Title'] = props.book.title
	if (props.book.isbn) items['ISBN'] = props.book.isbn
	if (props.book.price) items['Price'] = `€${props.book.price}`
	if (props.book.author) items['Author'] = props.book.author
	if (props.book.publisher) items['Publisher'] = props.book.publisher

	return Object.entries(items)
})

// webp not working...
const bookBg = thumbor(props.book.thumbnail).dimension(550, 0).url()
const bookBgUrl = computed(() => {
	return `url(${bookBg})`
})

// Add SEO
const pageMeta = [
	{ tag: 'link', tagItem: 'rel', tagValue: 'canonical', contentItem: 'href', contentValue: `https://lekker.nl/boek/${props.book.slug}` },
	{ tag: 'meta', tagItem: 'name', tagValue: 'title', contentItem: 'content', contentValue: props.book.title },
	{ tag: 'meta', tagItem: 'name', tagValue: 'description', contentItem: 'content', contentValue: props.book.description },
	{ tag: 'meta', tagItem: 'property', tagValue: 'article:published_time', contentItem: 'content', contentValue: props.book.published_at?.replace(' ', 'T') },
	{ tag: 'meta', tagItem: 'property', tagValue: 'og:title', contentItem: 'content', contentValue: props.book.title },
	{ tag: 'meta', tagItem: 'property', tagValue: 'og:description', contentItem: 'content', contentValue: props.book.description },
	{ tag: 'meta', tagItem: 'property', tagValue: 'og:image', contentItem: 'content', contentValue: thumbor(props.book.thumbnail).dimension(315, 415).filters('format(webp)').url() },
	{ tag: 'meta', tagItem: 'name', tagValue: 'twitter:title', contentItem: 'content', contentValue: `${props.book.title} - Lekker` },
	{ tag: 'meta', tagItem: 'name', tagValue: 'twitter:card', contentItem: 'content', contentValue: 'summary' },
	{ tag: 'meta', tagItem: 'name', tagValue: 'twitter:site', contentItem: 'content', contentValue: '@lekker500' },
]
</script>

<template>
	<Head>
		<title>{{ book.title }}</title>
		<link
			rel="preload"
			as="image"
			:href="bookBg"
		/>
		<component
			:is="`${meta.tag}`"
			v-for="(meta, metaIndex) in pageMeta"
			:key="`pagemeta-${metaIndex}`"
			:[meta.tagItem]="meta.tagValue"
			:[meta.contentItem]="meta.contentValue"
		/>

		<component
			:is="`script`"
			type="application/ld+json"
		>
			{{ book.schema }}
		</component>
	</Head>

	<main>
		<article
			class="book"
			:data-article-id="book.id"
		>
			<header class="bookheader container-xxl main-wrapper pt-lg-4">
				<div class="row justify-content-center">
					<div class="col-xl-10">
						<div class="book-header px-4 px-lg-0 mx-n4 mx-lg-0">
							<nav aria-label="breadcrumb">
								<client-only>
									<breadcrumb
										:nav-items="[
											{ label: 'Home', route: route('home') },
											{ label: 'Boeken', route: route('books') },
										]"
										:current="book.title"
										class="mt-4 mt-lg-0"
										:class="{
											'text-white': appStore.windowWidth <= appStore.breakpoints.lg,
										}"
									/>
								</client-only>
							</nav>
							<div class="head">
								<h1 class="ff-serif text-center text-lg-start">{{ book.title }}</h1>
								<p class="lead text-neutral-700 text-center text-lg-start mb-4">{{ book.author }}</p>
								<p class="lead mb-4">{{ book.description }}</p>
								<a
									v-if="book.price && book.order_url"
									class="btn btn-theme-500 fw-bold w-100 w-lg-auto"
									target="_blank"
									:href="book.order_url"
								>
									{{ $t('Order this book') }} &euro;{{ book.price }} <i class="str-arrow-right ms-1" />
								</a>
							</div>
							<thumbor-image
								:use="['sm', 'md']"
								:dimensions="{
									sm: [200, 0],
									md: [315, 415],
								}"
								:img="book.thumbnail"
								class="book-img text-center position-relative mb-0"
								:classes="{
									img: 'rounded',
								}"
								filters="format(webp)"
							/>
						</div>
					</div>
				</div>
			</header>

			<section class="container-xxl main-wrapper mt-lg-5">
				<div class="row justify-content-center">
					<div class="col-lg-9 col-xl-6">
						<hr class="my-4 d-lg-none" />
						<div v-html-raw="book.content"></div>
						<hr class="my-4 my-lg-5" />
						<div v-if="characteristics.length">
							<h5 class="mb-4">{{ $t('Characteristics') }}</h5>
							<dl class="data-list">
								<template
									v-for="(item, itemIndex) in characteristics"
									:key="`char-${itemIndex}`"
								>
									<dt>{{ $t(item[0]) }}</dt>
									<dd>{{ item[1] }}</dd>
								</template>
							</dl>
						</div>
					</div>
				</div>
			</section>
		</article>
	</main>

	<aside class="py-3 py-md-5 bg-neutral-200 mt-3">
		<div class="container-xxl main-wrapper">
			<div class="row justify-content-center">
				<div class="col-lg-8">
					<theme-base-swiper
						v-slot="slotProps"
						tag="section"
						:title="$t('Continue reading')"
						:items="moreBooks"
						:columns="{
							xs: 6,
							md: 4,
							xl: 3,
						}"
						url="/boeken"
						inline
					>
						<theme-card-book
							:item="slotProps.item"
							tag="article"
						/>
					</theme-base-swiper>
				</div>
			</div>
		</div>
	</aside>
</template>

<style lang="scss">
.bookheader {
	background-image: v-bind(bookBgUrl);
}
</style>
